import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    class: "subscriptions-view",
    "data-testid": "subscriptions-page"
};
const _hoisted_2 = { class: "subscriptions-header" };
const _hoisted_3 = {
    "data-testid": "page-title",
    class: "m-0 text-(length:--size-5xl) leading-[var(--line-height-5xl)] font-bold text-(--text-text-heading) dark:text-(--colors-text-white-default)"
};
const _hoisted_4 = {
    key: 0,
    class: "content-placeholder"
};
const _hoisted_5 = {
    key: 1,
    class: "content-placeholder"
};
const _hoisted_6 = {
    key: 2,
    class: "content-placeholder"
};
const _hoisted_7 = {
    key: 3,
    class: "subscriptions-content"
};
const _hoisted_8 = {
    key: 0,
    class: "content-placeholder"
};
import { ref, onMounted } from "vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { useI18n } from "vue-i18n";
import { useSubscriptions } from "@/shared/composables/useSubscriptions";
import { RecurringPaymentRuleUseCases } from "@/application/use-cases/RecurringPaymentRuleUseCases";
import { RecurringPaymentFrequency, } from "@/domain/entities/RecurringPaymentRule";
import { useAccountInfo } from "@/shared/composables/useAccountInfo";
import DashboardLoadingState from "@/presentation/components/dashboard/DashboardLoadingState.vue";
import DashboardErrorState from "@/presentation/components/dashboard/DashboardErrorState.vue";
import DashboardOnboardingIncomplete from "@/presentation/components/dashboard/DashboardOnboardingIncomplete.vue";
import SubscriptionsTable from "./subscriptions/SubscriptionsTable.vue";
import SubscriptionDetailModal from "./subscriptions/SubscriptionDetailModal.vue";
import CancelSubscriptionModal from "./subscriptions/CancelSubscriptionModal.vue";
import CancelNextTransactionModal from "./subscriptions/CancelNextTransactionModal.vue";
import UpdateSubscriptionModal from "./subscriptions/UpdateSubscriptionModal.vue";
import PauseSubscriptionModal from "./subscriptions/PauseSubscriptionModal.vue";
import ResumeSubscriptionModal from "./subscriptions/ResumeSubscriptionModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Subscriptions',
    setup(__props) {
        const { t } = useI18n();
        const route = useRoute();
        const router = useRouter();
        const recurringPaymentRuleUseCases = new RecurringPaymentRuleUseCases();
        const { isAccountActive, hasAccountInfo, error: accountError, isLoading: isLoadingAccount, fetchAccountInfo, } = useAccountInfo();
        const { listItems, isLoading, error, hasItems, isSearchActive, currentPage, pageSize, totalCount, loadList, cancelNext, cancel, update, pause, resume, goToPage, applyFilters, filterCounts, } = useSubscriptions();
        // Modal state
        const detailModalOpen = ref(false);
        const detailSubscription = ref(null);
        const isLoadingDetail = ref(false);
        const cancelModalOpen = ref(false);
        const cancelNextModalOpen = ref(false);
        const updateModalOpen = ref(false);
        const pauseModalOpen = ref(false);
        const resumeModalOpen = ref(false);
        const selectedSubscription = ref(null);
        const isCanceling = ref(false);
        const isCancelingNext = ref(false);
        const isUpdating = ref(false);
        const isPausing = ref(false);
        const isResuming = ref(false);
        // URL-driven detail modal handling
        onBeforeRouteUpdate((to) => {
            const ruleId = to.query.ruleId;
            if (ruleId) {
                openDetailModal(ruleId);
            }
            else if (detailModalOpen.value) {
                closeDetailModal();
            }
        });
        async function openDetailModal(ruleId) {
            isLoadingDetail.value = true;
            detailModalOpen.value = true;
            detailSubscription.value = null;
            const subscription = await recurringPaymentRuleUseCases.getById(ruleId);
            detailSubscription.value = subscription;
            isLoadingDetail.value = false;
        }
        async function closeDetailModal() {
            detailModalOpen.value = false;
            detailSubscription.value = null;
            await router.replace({ query: { ...route.query, ruleId: undefined } });
        }
        function handleViewDetails(item) {
            router.push({ query: { ...route.query, ruleId: item.id } });
        }
        async function retryInitialization() {
            await fetchAccountInfo();
            if (isAccountActive.value) {
                await loadList();
            }
        }
        onMounted(async () => {
            await fetchAccountInfo();
            if (isAccountActive.value) {
                await loadList();
                // Open detail modal if ruleId is in URL
                const ruleId = route.query.ruleId;
                if (ruleId) {
                    await openDetailModal(ruleId);
                }
            }
        });
        function handlePageSizeChange(size) {
            loadList(1, size);
        }
        function handleFiltersChange(filters) {
            applyFilters(filters);
        }
        function handleUpdate(item) {
            selectedSubscription.value = item;
            updateModalOpen.value = true;
        }
        function handleCancelNext(item) {
            selectedSubscription.value = item;
            cancelNextModalOpen.value = true;
        }
        function handleCancel(item) {
            selectedSubscription.value = item;
            cancelModalOpen.value = true;
        }
        function handlePause(item) {
            selectedSubscription.value = item;
            pauseModalOpen.value = true;
        }
        function handleResume(item) {
            selectedSubscription.value = item;
            resumeModalOpen.value = true;
        }
        function closeCancelModal() {
            cancelModalOpen.value = false;
            selectedSubscription.value = null;
        }
        async function confirmCancel(cancellationReason) {
            if (!selectedSubscription.value)
                return;
            isCanceling.value = true;
            try {
                const success = await cancel(selectedSubscription.value.id, cancellationReason);
                if (success) {
                    closeCancelModal();
                }
            }
            catch (err) {
                console.error("Failed to cancel subscription:", err);
            }
            finally {
                isCanceling.value = false;
            }
        }
        function closeCancelNextModal() {
            cancelNextModalOpen.value = false;
            selectedSubscription.value = null;
        }
        async function confirmCancelNext() {
            if (!selectedSubscription.value)
                return;
            isCancelingNext.value = true;
            try {
                const success = await cancelNext(selectedSubscription.value.id);
                if (success) {
                    closeCancelNextModal();
                }
            }
            catch (err) {
                console.error("Failed to cancel next transaction:", err);
            }
            finally {
                isCancelingNext.value = false;
            }
        }
        function closeUpdateModal() {
            updateModalOpen.value = false;
            selectedSubscription.value = null;
        }
        async function confirmUpdate(formData) {
            if (!selectedSubscription.value)
                return;
            isUpdating.value = true;
            try {
                const success = await update(selectedSubscription.value.id, formData);
                if (success) {
                    closeUpdateModal();
                }
            }
            catch (err) {
                console.error("Failed to update subscription:", err);
            }
            finally {
                isUpdating.value = false;
            }
        }
        function closePauseModal() {
            pauseModalOpen.value = false;
            selectedSubscription.value = null;
        }
        async function confirmPause() {
            if (!selectedSubscription.value)
                return;
            isPausing.value = true;
            try {
                const success = await pause(selectedSubscription.value.id);
                if (success) {
                    closePauseModal();
                }
            }
            catch (err) {
                console.error("Failed to pause subscription:", err);
            }
            finally {
                isPausing.value = false;
            }
        }
        function closeResumeModal() {
            resumeModalOpen.value = false;
            selectedSubscription.value = null;
        }
        async function confirmResume() {
            if (!selectedSubscription.value)
                return;
            isResuming.value = true;
            try {
                const success = await resume(selectedSubscription.value.id);
                if (success) {
                    closeResumeModal();
                }
            }
            catch (err) {
                console.error("Failed to resume subscription:", err);
            }
            finally {
                isResuming.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)("dashboard.menu.subscriptions")), 1 /* TEXT */)
                ]),
                (_unref(isLoadingAccount))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        _createVNode(DashboardLoadingState, { overlay: "" })
                    ]))
                    : (_unref(accountError) !== null)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createVNode(DashboardErrorState, {
                                title: _unref(t)('dashboard.loadAccountInfoError.title'),
                                message: _unref(t)('dashboard.loadAccountInfoError.message'),
                                "retry-label": _unref(t)('dashboard.loadAccountInfoError.retry'),
                                onRetry: retryInitialization
                            }, null, 8 /* PROPS */, ["title", "message", "retry-label"])
                        ]))
                        : (_unref(hasAccountInfo) && !_unref(isAccountActive))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createVNode(DashboardOnboardingIncomplete, {
                                    title: _unref(t)('dashboard.onboardingIncomplete.title'),
                                    message: _unref(t)('dashboard.onboardingIncomplete.message'),
                                    "action-label": _unref(t)('dashboard.onboardingIncomplete.action'),
                                    onAction: _cache[0] || (_cache[0] = ($event) => (_unref(router).push('/onboarding')))
                                }, null, 8 /* PROPS */, ["title", "message", "action-label"])
                            ]))
                            : (_unref(isAccountActive))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                    (_unref(error))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                            _createVNode(DashboardErrorState, {
                                                title: _unref(t)('dashboard.subscriptions.loadError.title'),
                                                message: _unref(t)('dashboard.subscriptions.loadError.message'),
                                                "retry-label": _unref(t)('dashboard.subscriptions.loadError.retry'),
                                                onRetry: _unref(loadList)
                                            }, null, 8 /* PROPS */, ["title", "message", "retry-label", "onRetry"])
                                        ]))
                                        : (_openBlock(), _createBlock(SubscriptionsTable, {
                                            key: 1,
                                            "list-items": _unref(listItems),
                                            "is-loading": _unref(isLoading),
                                            "has-items": _unref(hasItems),
                                            "is-search-active": _unref(isSearchActive),
                                            "current-page": _unref(currentPage),
                                            "page-size": _unref(pageSize),
                                            "total-count": _unref(totalCount),
                                            "filter-counts": _unref(filterCounts),
                                            onViewDetails: handleViewDetails,
                                            onUpdate: handleUpdate,
                                            onPause: handlePause,
                                            onResume: handleResume,
                                            onCancelNext: handleCancelNext,
                                            onCancel: handleCancel,
                                            onGoToPage: _unref(goToPage),
                                            onPageSizeChange: handlePageSizeChange,
                                            onFiltersChange: handleFiltersChange
                                        }, null, 8 /* PROPS */, ["list-items", "is-loading", "has-items", "is-search-active", "current-page", "page-size", "total-count", "filter-counts", "onGoToPage"]))
                                ]))
                                : _createCommentVNode("v-if", true),
                _createVNode(SubscriptionDetailModal, {
                    "is-open": detailModalOpen.value,
                    subscription: detailSubscription.value,
                    "is-loading": isLoadingDetail.value,
                    onClose: closeDetailModal
                }, null, 8 /* PROPS */, ["is-open", "subscription", "is-loading"]),
                _createVNode(CancelSubscriptionModal, {
                    "is-open": cancelModalOpen.value,
                    "subscription-id": selectedSubscription.value?.id || '',
                    "shopper-reference": selectedSubscription.value?.shopperReference || '',
                    "merchant-reference": selectedSubscription.value?.merchantReference || '',
                    "is-loading": isCanceling.value,
                    onClose: closeCancelModal,
                    onConfirm: confirmCancel
                }, null, 8 /* PROPS */, ["is-open", "subscription-id", "shopper-reference", "merchant-reference", "is-loading"]),
                _createVNode(CancelNextTransactionModal, {
                    "is-open": cancelNextModalOpen.value,
                    "subscription-id": selectedSubscription.value?.id || '',
                    "shopper-reference": selectedSubscription.value?.shopperReference || '',
                    "merchant-reference": selectedSubscription.value?.merchantReference || '',
                    "next-transaction-date": selectedSubscription.value?.nextTransactionDate || null,
                    "is-loading": isCancelingNext.value,
                    onClose: closeCancelNextModal,
                    onConfirm: confirmCancelNext
                }, null, 8 /* PROPS */, ["is-open", "subscription-id", "shopper-reference", "merchant-reference", "next-transaction-date", "is-loading"]),
                _createVNode(UpdateSubscriptionModal, {
                    "is-open": updateModalOpen.value,
                    "subscription-id": selectedSubscription.value?.id || '',
                    "shopper-reference": selectedSubscription.value?.shopperReference || '',
                    "merchant-reference": selectedSubscription.value?.merchantReference || '',
                    "current-amount": selectedSubscription.value?.amount || 0,
                    "current-tax-amount": selectedSubscription.value?.taxAmount || 0,
                    "current-currency": selectedSubscription.value?.currency || '',
                    "current-frequency": selectedSubscription.value?.frequency || _unref(RecurringPaymentFrequency).Monthly,
                    "current-transaction-count": selectedSubscription.value?.transactionCount || null,
                    "completed-transaction-count": selectedSubscription.value?.transactionsCompleted || 0,
                    "is-loading": isUpdating.value,
                    onClose: closeUpdateModal,
                    onConfirm: confirmUpdate
                }, null, 8 /* PROPS */, ["is-open", "subscription-id", "shopper-reference", "merchant-reference", "current-amount", "current-tax-amount", "current-currency", "current-frequency", "current-transaction-count", "completed-transaction-count", "is-loading"]),
                _createVNode(PauseSubscriptionModal, {
                    "is-open": pauseModalOpen.value,
                    "subscription-id": selectedSubscription.value?.id || '',
                    "shopper-reference": selectedSubscription.value?.shopperReference || '',
                    "merchant-reference": selectedSubscription.value?.merchantReference || '',
                    "is-loading": isPausing.value,
                    onClose: closePauseModal,
                    onConfirm: confirmPause
                }, null, 8 /* PROPS */, ["is-open", "subscription-id", "shopper-reference", "merchant-reference", "is-loading"]),
                _createVNode(ResumeSubscriptionModal, {
                    "is-open": resumeModalOpen.value,
                    "subscription-id": selectedSubscription.value?.id || '',
                    "shopper-reference": selectedSubscription.value?.shopperReference || '',
                    "merchant-reference": selectedSubscription.value?.merchantReference || '',
                    "is-loading": isResuming.value,
                    onClose: closeResumeModal,
                    onConfirm: confirmResume
                }, null, 8 /* PROPS */, ["is-open", "subscription-id", "shopper-reference", "merchant-reference", "is-loading"])
            ]));
        };
    }
});
